<template>
  <div>
    <h4 ref="page-heading" class="main-header" tabindex="-1">{{ $t('navigation_plans') }}</h4>
    <v-row ref="scroll-outer" class="upgrade" align-content="center" justify="center">
      <v-col ref="scroll-inner" cols="12" lg="12" class="upgrade-wrapper">
        <div class="billing-cycle-selector d-flex flex-column align-center ma-5">
          <div class="wrapper font-weight-medium" role="group" :aria-label="$t('plans_toggle_group_label')">
            <button
              class="px-4 py-2"
              :class="{ 'selected': billingCycle === 'month' }"
              :aria-pressed="String(billingCycle === 'month')"
              @click="updateBillingCycle('month')">{{ $t('monthly') }}</button>
            <button
              class="px-4 py-2"
              :class="{ 'selected': billingCycle === 'year' }"
              :aria-pressed="String(billingCycle === 'year')"
              @click="updateBillingCycle('year')">{{ $t('yearly') }}</button>
          </div>
          <v-chip small class="billing-cycle-note mt-3">{{ $t('plans_yearly_billing_note', { percent: 25 }) }}</v-chip>
        </div>
        <template v-if="view === 'plans'">
          <div class="pricing-table mt-4">
            <plan-card
              :name="$t('plans_dpp_textile_name')"
              :tagline="$t('plans_dpp_textile_tagline')"
              :price="dppTextilePrice"
              :pricePeriod="$t('plans_price_period_month')"
              :priceNote="dppTextilePriceNote"
              :quotas="dppTextileQuotas"
              :featuresTitle="$t('plans_dpp_textile_features')"
              :features="dppTextileFeatures"
              :ctaLabel="dppTextileCtaLabel"
              :ctaDisabled="isLoading && !isSubscribedToDppTextile"
              :loading="isLoading"
              @cta="onDppTextileCta">
              <template v-if="isTestPlanAvailable" #after-cta>
                <v-btn class="plan-staging-cta mt-2" @click="upgrade('st-pro', true)" :disabled="isLoading || currentPlan?.id === 'st-pro'" color="primary" text small>Staging only 1 day plan</v-btn>
              </template>
            </plan-card>
            <plan-card
              :name="$t('plans_enterprise_name')"
              :tagline="$t('plans_enterprise_tagline')"
              :price="$t('custom_pricing')"
              :priceNote="$t('plans_enterprise_price_note')"
              :quotas="enterpriseQuotas"
              :featuresTitle="$t('plans_enterprise_features')"
              :features="enterpriseFeatures"
              :ctaLabel="$t('contact_us')"
              ctaVariant="outlined"
              @cta="onEnterpriseCta">
            </plan-card>
          </div>
          <div class="d-flex justify-center mt-4">
            <v-btn text color="primary" @click="showComparison">{{ $t('compare_all_features') }}</v-btn>
          </div>
        </template>

        <template v-else>
          <h5 ref="comparison-heading" class="d-sr-only" tabindex="-1">{{ $t('compare_all_features') }}</h5>
          <transition name="comparison" appear>
            <plan-comparison-table
              :groups="comparisonGroups"
              :caption="$t('plans_comparison_caption')"
              :includedLabel="$t('plans_feature_included')"
              :notIncludedLabel="$t('plans_feature_not_included')"
              :dppTextileName="$t('plans_dpp_textile_name')"
              :dppTextilePrice="dppTextilePrice"
              :dppTextilePricePeriod="$t('plans_price_period_month')"
              :dppTextilePriceNote="comparisonPriceNote"
              :dppTextileCtaLabel="comparisonCtaLabel"
              :dppTextileCtaDisabled="isLoading && !isSubscribedToDppTextile"
              :loading="isLoading"
              :loadingLabel="$t('plans_price_loading')"
              :enterpriseName="$t('plans_enterprise_name')"
              :enterprisePrice="$t('custom_pricing')"
              :enterpriseCtaLabel="$t('contact_us')"
              @dpp-textile-cta="onDppTextileCta"
              @enterprise-cta="onEnterpriseCta">
            </plan-comparison-table>
          </transition>
          <div class="d-flex justify-center mt-4">
            <v-btn text color="primary" @click="showPlans">{{ $t('view_plans') }}</v-btn>
          </div>
        </template>
      </v-col>

      <checkout-dialog
        v-if="profile?.company?.id && isUpgradePlanOpen"
        :openModal="isUpgradePlanOpen"
        :selectedPlan="selectedPlan"
        ref="paddle-checkout-dialog"
        @close="handleCheckoutPopupClose"
        :prices="prices"
        :contract="contract"
        :isYearlyPayment="billingCycle === 'year'"
        >
      </checkout-dialog>

      <!-- contact us dialog -->
      <v-dialog v-model="contactUsDialog" width="600">
        <v-card>
          <v-card-title class="d-flex align-center">
            {{ $t('contact_us') }}
            <v-spacer></v-spacer>
            <v-btn icon :aria-label="$t('close')" @click="contactUsDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-0 px-6 pb-6 black--text">
            <div v-if="contactFormFailed">
              <p>{{ $t('contact_form_error') }}</p>
              <div v-html="$t('contact_us_dialog')"></div>
            </div>
            <div v-else class="contact-form" :class="{ 'contact-form--loading': isContactFormLoading }">
              <div v-if="isContactFormLoading" class="contact-form-loading">
                <v-progress-circular indeterminate color="primary" role="status" :aria-label="$t('contact_form_loading')"></v-progress-circular>
              </div>
              <div id="hubspotForm" :key="contactFormKey"></div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </div>
</template>

<script>

import CheckoutDialog from '@/components/upgrade/CheckoutDialog'
import PlanCard from '@/components/upgrade/PlanCard'
import PlanComparisonTable from '@/components/upgrade/PlanComparisonTable'
import Plans from '@/const/plans'
import PlanComparison from '@/const/plan-comparison'
import Api from '@/api'
import Utils from '@/utils/utils'
import { mapGetters } from 'vuex'
import amplitude from 'amplitude-js'
import paddleService from '@/services/paddle'
import hubspotService from '@/services/hubspot'

export default {
  name: 'Upgrade',
  computed: {
    ...mapGetters(['currentPlan', 'profile', 'contract', 'subscription', 'usedCodesCount']),

    isTestPlanAvailable () {
      return Api.paddle_plans['st-pro'].day
    },

    entrySource () {
      return this.$route.query.source || 'direct'
    },

    accountStatus () {
      const plan = this.currentPlan
      if (!plan) {
        return 'unknown'
      }
      if (plan.id === 'st-pro-trial') {
        return 'trial'
      }
      if (plan.cancellation_date && plan.valid_to) {
        const now = Date.now()
        if (now >= new Date(plan.valid_to).getTime() && now < new Date(plan.cancellation_date).getTime()) {
          return 'grace_period'
        }
      }
      if (String(plan.id).startsWith('st-pro-free')) {
        return 'expired'
      }
      return 'active'
    },

    billingToggle () {
      return this.billingCycle === 'year' ? 'yearly' : 'monthly'
    },

    dppTextilePlan () {
      return this.getPlan('st-pro')
    },

    dppTextilePrice () {
      if (this.billingCycle === 'year') {
        return paddleService.calculateMonthlyEquivalent(this.loadedPlans['st-pro'], this.currencyCode, this.$i18n.locale) || '-'
      }
      return this.formatPlanPrice('month') || '-'
    },

    dppTextilePriceNote () {
      if (this.billingCycle === 'year') {
        return this.$t('plans_price_note_annual', { amount: this.formatPlanPrice('year') || '-' })
      }
      return this.$t('plans_price_note_monthly')
    },

    planLimits () {
      return [
        {
          label: this.$t('plans_product_passports'),
          dppTextile: this.dppTextileQuota,
          enterprise: this.$t('plans_custom_volume')
        },
        {
          label: this.$t('plans_seats'),
          dppTextile: String(this.dppTextilePlan.seats),
          enterprise: this.$t('unlimited')
        },
        {
          label: this.$t('plans_scans'),
          dppTextile: this.$t('unlimited'),
          enterprise: this.$t('unlimited')
        }
      ]
    },

    dppTextileQuotas () {
      return this.planLimits.map((limit) => ({ label: limit.label, value: limit.dppTextile }))
    },

    dppTextileQuota () {
      return Utils.formatNumber(this.dppTextilePlan.quota, this.$i18n.locale)
    },

    dppTextileFeatures () {
      return this.dppTextilePlan.extras.map((key) => this.$t(key))
    },

    isSubscribedToDppTextile () {
      return this.currentPlan?.id === 'st-pro'
    },

    dppTextileCtaLabel () {
      return this.isSubscribedToDppTextile ? this.$t('manage_subscription') : this.$t('subscribe')
    },

    enterpriseQuotas () {
      return this.planLimits.map((limit) => ({ label: limit.label, value: limit.enterprise }))
    },

    enterpriseFeatures () {
      return this.getPlan('enterprise').extras.map((key) => this.$t(key))
    },

    comparisonPriceNote () {
      if (this.isLoading || this.billingCycle !== 'year') {
        return ''
      }
      return this.dppTextilePriceNote
    },

    comparisonCtaLabel () {
      return this.isSubscribedToDppTextile ? this.$t('manage') : this.$t('subscribe')
    },

    comparisonGroups () {
      return [this.comparisonLimitsGroup, ...this.comparisonFeatureGroups]
    },

    comparisonLimitsGroup () {
      return {
        title: this.$t('plans_group_limits'),
        rows: this.planLimits
      }
    },

    comparisonFeatureGroups () {
      return PlanComparison.map((group) => ({
        title: this.$t(group.titleKey),
        rows: group.rows.map((row) => ({
          label: this.$t(row.labelKey),
          dppTextile: this.translateCell(row.dppTextile),
          enterprise: this.translateCell(row.enterprise)
        }))
      }))
    }
  },
  components: {
    CheckoutDialog,
    PlanCard,
    PlanComparisonTable
  },
  data () {
    return {
      selectedPlan: null,
      isUpgradePlanOpen: false,
      billingCycle: 'month',
      prices: {
        'st-pro': {
          month: null,
          year: null,
          day: null
        }
      },
      loadedPlans: {},
      currencyCode: null,
      view: 'plans',
      contactUsDialog: false,
      isContactFormLoading: false,
      contactFormKey: 0,
      contactFormFailed: false,
      plans: Plans,
      yearlySavings: {},
      isLoading: false,
      removeEventCallback: null,
      pricingLoaded: false
    }
  },
  async mounted () {
    paddleService.initialize()

    this.removeEventCallback = paddleService.addEventCallback((event) => {
      if (this.$refs['paddle-checkout-dialog']) {
        this.$refs['paddle-checkout-dialog'].eventHandler(event)
      }
    })

    this.updatePrices()

    amplitude.getInstance().logEvent('Plans Page Viewed', {
      source: this.entrySource,
      account_status: this.accountStatus,
      plan_shown: 'dpp_textile_enterprise'
    })
  },
  beforeDestroy () {
    if (this.removeEventCallback) {
      this.removeEventCallback()
    }
  },
  methods: {
    updateBillingCycle (cycle) {
      if (cycle === this.billingCycle) {
        return
      }
      const from = this.billingToggle
      this.billingCycle = cycle
      amplitude.getInstance().logEvent('Billing Toggle Switched', { from, to: this.billingToggle })
    },

    trackPlanCta (ctaType, planId) {
      amplitude.getInstance().logEvent('Plan CTA Clicked', {
        source: this.entrySource,
        cta_type: ctaType,
        plan_id: planId,
        billing_toggle: this.billingToggle
      })
    },

    getPlan (planId) {
      return this.plans.find((plan) => plan.id === planId)
    },

    // Paddle returns totals in minor units already formatted for its own locale;
    // re-format from the raw amount so every price follows the UI locale.
    formatPlanPrice (cycle) {
      return paddleService.formatLineItemTotal(this.loadedPlans['st-pro']?.[cycle], this.currencyCode, this.$i18n.locale)
    },

    translateCell (value) {
      return typeof value === 'boolean' ? value : this.$t(value)
    },

    showComparison () {
      amplitude.getInstance().logEvent('Compare Features Opened', { source: this.entrySource })
      this.view = 'comparison'
      this.resetScroll()
      this.focusHeading('comparison-heading')
    },

    showPlans () {
      this.view = 'plans'
      this.resetScroll()
      this.focusHeading('page-heading')
    },

    resetScroll () {
      this.$nextTick(() => {
        ['scroll-outer', 'scroll-inner'].forEach((name) => {
          const target = this.$refs[name]
          const element = target?.$el || target
          if (element) {
            element.scrollTop = 0
          }
        })
      })
    },

    focusHeading (ref) {
      this.$nextTick(() => {
        const heading = this.$refs[ref]
        if (heading) {
          heading.focus()
        }
      })
    },

    onDppTextileCta () {
      if (this.isSubscribedToDppTextile) {
        this.trackPlanCta('manage_subscription', 'st-pro')
        this.$router.push({ name: 'profile.subscription' })
        return
      }
      if (this.upgrade('st-pro')) {
        this.trackPlanCta('subscribe', 'st-pro')
      }
    },

    onEnterpriseCta () {
      this.trackPlanCta('contact_us', 'enterprise')
      this.contactUsDialog = true
      this.loadContactForm()
    },

    async loadContactForm () {
      if (this.isContactFormLoading) {
        return
      }
      this.isContactFormLoading = true
      this.contactFormFailed = false
      this.contactFormKey += 1
      try {
        await this.$nextTick()
        await hubspotService.createContactForm('#hubspotForm')
      } catch (error) {
        console.error(`Error loading contact form: ${error.message}`)
        this.contactFormFailed = true
      } finally {
        this.isContactFormLoading = false
      }
    },

    formatPrices (prices) {
      for (const planKey in prices) {
        for (const cycleKey in prices[planKey]) {
          if (prices[planKey][cycleKey].indexOf('.') !== -1 && prices[planKey][cycleKey].split('.')[1] === '00') {
            prices[planKey][cycleKey] = prices[planKey][cycleKey].split('.')[0]
          }
        }
      }
      return prices
    },

    async updatePrices () {
      this.isLoading = true
      try {
        const { prices, loadedPlans, currencyCode } = await paddleService.getPricePreviews({
          customerId: this.profile.company?.external_id,
          addressId: this.subscription?.address_id,
          businessId: this.subscription?.business_id
        })
        this.prices = this.formatPrices(prices)
        this.loadedPlans = loadedPlans
        this.currencyCode = currencyCode
        this.pricingLoaded = true
      } catch (error) {
        console.error(`Error updating prices: ${error.message}`)
      } finally {
        this.isLoading = false
      }
    },

    upgrade (planID, isTestPlan) {
      const billingCycle = isTestPlan ? 'day' : this.billingCycle
      const loadedPlan = this.loadedPlans[planID]?.[billingCycle]
      if (!loadedPlan) {
        return false
      }
      this.selectedPlan = loadedPlan
      this.selectedPlan.cost = this.prices[planID]
      this.selectedPlan.id = planID
      this.isUpgradePlanOpen = true
      return true
    },

    async handleCheckoutPopupClose (isPaymentFullfilled) {
      this.isUpgradePlanOpen = false
      if (isPaymentFullfilled) {
        const user = await this.$store.dispatch('loadUserProfile')
        await this.$store.dispatch('loadUserContract', { id: user.plan?.contract_id })
        this.$store.dispatch('loadQuotas')
        this.$store.dispatch('loadSubscription')
      }
    }
  }
}
</script>

<style lang="sass" scoped>

@import '@/variables.sass'

.pricing-table
  display: flex
  flex-direction: column
  gap: 40px
  max-width: 980px
  margin-left: auto
  margin-right: auto
  @include lg
    display: grid
    grid-template-columns: repeat(2, minmax(0, 1fr))
    grid-template-rows: repeat(5, auto)
    column-gap: 40px
    row-gap: 0
    position: relative
    isolation: isolate
    &::before,
    &::after
      content: ''
      position: absolute
      top: 0
      bottom: 0
      width: calc(50% - 20px)
      z-index: -1
      background: white
      border-radius: 6px
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.1)
    &::before
      left: 0
    &::after
      right: 0

.plan-staging-cta
  align-self: flex-start

.comparison-enter-active
  transition: opacity 0.25s ease, transform 0.25s ease

.comparison-enter
  opacity: 0
  transform: translateY(8px)

.comparison-enter-to
  opacity: 1
  transform: translateY(0)

@media (prefers-reduced-motion: reduce)
  .comparison-enter-active
    transition: none

.billing-cycle-selector
  .wrapper
    border-radius: 4px
    background: #F1F1F1
    button
      margin: 3px
      &.selected
        color: $primary
        background: white

.billing-cycle-note
  background: rgba($primary, 0.1) !important
  border-radius: 12px
  font-size: 12px

.upgrade
  overflow: auto
  height: calc(100vh - var(--banner-height, 0px))
  margin-top: 0

.upgrade-wrapper
  height: 100%
  overflow: auto
  padding-top: 0
  padding-bottom: 80px
  padding-right: 0px

.link
  color: $primary
  cursor: pointer

.feature-list
  border-top: none
  text-align: left
  li
    padding-bottom: 8px

.italic
  font-style: italic

.premium
  background: #ecf9ffff
  border-left: none
  box-shadow: 0 4px 16px rgba(75,153,202,0.12)
  border-color: rgba(75,153,202,0.15)

.disclaimer
  font-size: 11px
  color: #555555

.detail-row
  font-size: 16px

.contact-form
  position: relative
  margin-top: 15px
  &--loading
    min-height: 160px
    #hubspotForm
      visibility: hidden

.contact-form-loading
  position: absolute
  top: 0
  right: 0
  bottom: 0
  left: 0
  display: flex
  align-items: center
  justify-content: center

</style>
